// Kit routes (WI-42, run plan §3): a ready route a destination kit offers on an empty trip, "Use
// this route, change dates". Each stop is a stay on its own island with a usual length; the route
// is stretched or squeezed to the trip's dates, and the usual way between stops becomes an untimed
// journey on each change-over (domain/trip/stays).
import type { LegKind } from '../../../shared/pack/schema'
import { addDays, daysBetween } from '../time'
import type { Stay } from './stays'

export interface RouteStop {
  name: string
  spot: string
  regionId: string
  anchor: [number, number]
  /** Usual nights there. */
  nights: number
  /** How you usually get here from the stop before. */
  how?: LegKind
  /** A suggested Trip-tab route piece for the way in (see pieceHint). */
  piece?: PieceHint
  timezone?: string
}

export interface KitRoute {
  id: string
  title: string
  /** The kits that offer it (the trip's kit first picks the routes shown). */
  kits: readonly string[]
  stops: readonly RouteStop[]
}

export const KIT_ROUTES: readonly KitRoute[] = [
  {
    id: 'hokkaido',
    title: 'Hokkaido',
    kits: ['region-hokkaido', 'region-japan'],
    stops: [
      { name: 'Sapporo', spot: 'sapporo', regionId: 'sapporo', anchor: [43.0681, 141.3508], nights: 3 },
      // Over the pass to Yōtei: the viaduct over the mini mountain.
      { name: 'Niseko', spot: 'niseko', regionId: 'base', anchor: [42.8635, 140.6981], nights: 5, how: 'train', piece: 'viaduct' },
      { name: 'Otaru', spot: 'otaru', regionId: 'otaru', anchor: [43.1979, 141.001], nights: 2, how: 'bus' },
    ],
  },
  {
    id: 'japan-classic',
    title: 'Japan classic',
    kits: ['region-japan'],
    stops: [
      { name: 'Tokyo', spot: 'tokyo', regionId: 'tokyo', anchor: [35.6812, 139.7671], nights: 4 },
      // The shinkansen past Fuji: the viaduct over the mini mountain.
      { name: 'Kyoto', spot: 'kyoto', regionId: 'kyoto', anchor: [35.0116, 135.7681], nights: 3, how: 'train', piece: 'viaduct' },
      { name: 'Osaka', spot: 'osaka', regionId: 'osaka', anchor: [34.6937, 135.5023], nights: 2, how: 'train' },
      // No Hiroshima island yet: any Japanese city, with Miyajima's weather next door.
      { name: 'Hiroshima', spot: 'bigcity', regionId: 'miyajima', anchor: [34.3853, 132.4553], nights: 1, how: 'train', piece: 'tunnel' },
      { name: 'Miyajima', spot: 'miyajima', regionId: 'miyajima', anchor: [34.2959, 132.3197], nights: 1, how: 'ferry' },
    ],
  },
  // Run plan §8 #8: more routes, with route-piece hints (pieceHint).
  {
    id: 'tokyo-fuji',
    title: 'Tokyo, Fuji and the coast',
    kits: ['region-japan'],
    stops: [
      { name: 'Tokyo', spot: 'tokyo', regionId: 'tokyo', anchor: [35.6812, 139.7671], nights: 3 },
      { name: 'Hakone', spot: 'fuji', regionId: 'fuji', anchor: [35.2324, 139.1069], nights: 2, how: 'train', piece: 'viaduct' },
      { name: 'Kamakura', spot: 'kamakura', regionId: 'kamakura', anchor: [35.3192, 139.5467], nights: 2, how: 'train', piece: 'tunnel' },
    ],
  },
  {
    id: 'kansai',
    title: 'Kyoto, Nara and Osaka',
    kits: ['region-japan'],
    stops: [
      { name: 'Kyoto', spot: 'kyoto', regionId: 'kyoto', anchor: [35.0116, 135.7681], nights: 3 },
      { name: 'Nara', spot: 'nara', regionId: 'nara', anchor: [34.6851, 135.8048], nights: 1, how: 'train' },
      { name: 'Osaka', spot: 'osaka', regionId: 'osaka', anchor: [34.6937, 135.5023], nights: 2, how: 'train', piece: 'loop' },
    ],
  },
  {
    id: 'alps',
    title: 'Tokyo, the Alps and Kyoto',
    kits: ['region-japan'],
    stops: [
      { name: 'Tokyo', spot: 'tokyo', regionId: 'tokyo', anchor: [35.6812, 139.7671], nights: 3 },
      { name: 'Shirakawa-go', spot: 'shirakawago', regionId: 'shirakawago', anchor: [36.2578, 136.9062], nights: 1, how: 'bus', piece: 'tunnel' },
      { name: 'Kyoto', spot: 'kyoto', regionId: 'kyoto', anchor: [35.0116, 135.7681], nights: 3, how: 'bus' },
    ],
  },
  {
    id: 'south',
    title: 'Fukuoka and Okinawa',
    kits: ['region-japan'],
    stops: [
      { name: 'Fukuoka', spot: 'fukuoka', regionId: 'fukuoka', anchor: [33.5902, 130.4017], nights: 3 },
      { name: 'Okinawa', spot: 'okinawa', regionId: 'okinawa', anchor: [26.2124, 127.6809], nights: 4, how: 'flight' },
    ],
  },
  {
    id: 'korea',
    title: 'Korea',
    kits: ['region-korea'],
    stops: [
      { name: 'Seoul', spot: 'seoul', regionId: 'seoul', anchor: [37.5665, 126.978], nights: 4, timezone: 'Asia/Seoul' },
      { name: 'Han River', spot: 'hangang', regionId: 'seoul', anchor: [37.5283, 126.9326], nights: 2, how: 'train', timezone: 'Asia/Seoul' },
    ],
  },
]

/** The routes a trip's kit offers (none for "Somewhere else"). */
export const routesFor = (kit: string | undefined): KitRoute[] => (kit ? KIT_ROUTES.filter((r) => r.kits.includes(kit)) : [])

/** "Sapporo → Niseko → Otaru". */
export const routeLine = (r: KitRoute) => r.stops.map((s) => s.name).join(' → ')

/** Each stop's nights fitted to `total`: the usual split, scaled, at least one night each. */
export function fitNights(usual: readonly number[], total: number): number[] {
  const n = usual.length
  if (total <= n) return usual.map(() => 1)
  const sum = usual.reduce((a, b) => a + b, 0)
  const out = usual.map((u) => Math.max(1, Math.round((u * total) / sum)))
  // Rounding leftovers go to (or come from) the longest stays first.
  const order = out.map((_, i) => i).sort((a, b) => usual[b]! - usual[a]!)
  let diff = total - out.reduce((a, b) => a + b, 0)
  for (let k = 0; diff !== 0 && k < 1000; k++) {
    const i = order[k % n]!
    if (diff > 0) {
      out[i]!++
      diff--
    } else if (out[i]! > 1) {
      out[i]!--
      diff++
    }
  }
  return out
}

/** The route as stays over the trip's dates (`ids` names each new stage). */
export function routeStays(route: KitRoute, startDate: string, endDate: string, ids: (i: number) => string): Stay[] {
  const split = fitNights(
    route.stops.map((s) => s.nights),
    Math.max(0, daysBetween(startDate, endDate)),
  )
  let at = startDate
  return route.stops.map((s, i) => {
    const checkIn = at
    at = addDays(at, split[i]!)
    return { id: ids(i), name: s.name, checkIn, checkOut: at, spot: s.spot, regionId: s.regionId, anchor: s.anchor, ...(s.timezone ? { timezone: s.timezone } : {}) }
  })
}

/**
 * The Trip tab's route pieces a kit route suggests (lane 1's PieceKind in src/stage/trip/route.ts):
 * a tunnel through the cloud, a loop, or a viaduct over a mini mountain. A ferry's sky canal comes
 * with the ferry, so it's never a hint.
 */
export type PieceHint = 'tunnel' | 'loop' | 'viaduct'

/**
 * The hinted piece for a journey from one island to the next ("tokyo" → "fuji": the viaduct over
 * the mountain), from every kit route that makes that hop. The Trip tab picks it when it fits the
 * link (a rail ride where the piece goes); otherwise its seeded pick stands.
 */
export function pieceHint(fromSpot: string, toSpot: string): PieceHint | undefined {
  for (const r of KIT_ROUTES)
    for (let i = 1; i < r.stops.length; i++) if (r.stops[i - 1]!.spot === fromSpot && r.stops[i]!.spot === toSpot && r.stops[i]!.piece) return r.stops[i]!.piece
  return undefined
}
